import { getUserInfo } from '../client/client';
import { toLocalEndOfDay, toLocalStartOfDay } from '../client/matteroomTab/Utils';
import axiosTemp from './axiosTemp';
import { getStoredRefreshToken } from '@features/auth/utils/storage';
import { consumeDuplicateTimeEntryIdsPayload } from '../client/utils/duplicateEntry';

export function toApiDataArray(response) {
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.results)) return response.results;
    return [];
}

export async function getLoginURL(userEmail = getUserInfo().email, authKey,loginType) {
 
    try {
        const response = await axiosTemp().get(`/api/v1.0/get_login_url/`, {
            params: {
                "http-login-type-options": loginType,
                username: userEmail,
                ...(authKey && { auth_key: authKey }),
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching login error:', error);
        throw error;
    }
}

export async function refreshToken() {
    try {
        const response = await axiosTemp().post(`/api/v1.0/refresh_token/`, {
            token: getStoredRefreshToken(),
        });
        return response.data;
    } catch (error) {
        console.error('Error refreshing token:', error);
        throw error;
    }
}

export async function geto365Token(token) {
    try {
        const response = await axiosTemp().get(`/api/v1.0/login_thirdparty/?token=${token}`, {
            headers: {
                'LOGIN-TYPE-OPTIONS': 'o365',
                'TimeZone':Intl.DateTimeFormat().resolvedOptions().timeZone,
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching login error:', error);
        throw error;
    }
}

export function getLoginCodeFromThirdParty(code, username, type = 'o365', state = null) {
    const stateParam = state ? `&state=${state}` : '';

    return axiosTemp().get(
        `/api/v1.0/login_thirdparty/?code=${code}&username=${username}${stateParam}`,
        {
            headers: {
                'LOGIN-TYPE-OPTIONS': type,
                'TimeZone': Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
        }
    );
}

export async function getTimeEntries(query, signal) {
  try {
      const response = await axiosTemp().get(`/api/v1.0/accounts/ACCOUNT_ID/time-entries/${query}`, { signal });
      return response.data;
  } catch (error) {
      console.error('Error fetching time entries:', error);
      throw error;
  }
}

export async function getTimeEntry(id) {
  try {
      const response = await axiosTemp().get(`/api/v1.0/accounts/ACCOUNT_ID/time-entries/${id}/`);
      return response.data;
  } catch (error) {
      console.error('Error fetching time entries:', error);
      throw error;
  }
}

export async function createTimeEntry(payload) {
    return  await axiosTemp().post(
        `/api/v1.0/accounts/ACCOUNT_ID/time-entries/`,
        JSON.stringify({ ...payload, ...consumeDuplicateTimeEntryIdsPayload() }),
    );
}

export async function deleteTimeEntries(payload) {
  return await axiosTemp().delete(`/api/batch/v1.0/accounts/ACCOUNT_ID/time-entries/`, {
    data: payload,
  });
}

export async function updateTimeEntries(id, payload) {
    if(payload.hasOwnProperty("start")){
        payload['dt'] = payload['start'];
    }
    const response = await axiosTemp().patch(
        `/api/v1.0/accounts/ACCOUNT_ID/time-entries/${id}/`,
        JSON.stringify({ ...payload, ...consumeDuplicateTimeEntryIdsPayload() }),
    );

    return response.data;
}

export async function batchUpdateTimeEntries(payload) {
    return  await axiosTemp().patch(
        `/api/v1.0/accounts/ACCOUNT_ID/time-entries/`,
        JSON.stringify(payload), 
    );
}

export async function mergeTimeEntries(payload) {
    return  await axiosTemp().post(
        `/api/v1.0/accounts/ACCOUNT_ID/time-entries/merge/`,
        JSON.stringify({ ...payload, ...consumeDuplicateTimeEntryIdsPayload() }),
    );
}

export async function mergeTimeEntriesSuggest(payload) {
    return  await axiosTemp().post(
        `/api/v1.0/accounts/ACCOUNT_ID/time-entries/merge/suggest/`,
        JSON.stringify(payload), 
    );
}

export async function getTimeEntryGroups(groupIds, delegators) {
    if (!Array.isArray(groupIds) || groupIds.length === 0) return [];
    const chunks = [];
    for (let index = 0; index < groupIds.length; index += 200) {
        chunks.push(groupIds.slice(index, index + 200));
    }
    const responses = await Promise.all(
        chunks.map(chunk => axiosTemp().get(
            `/api/v1.0/accounts/ACCOUNT_ID/time-entry-groups/`,
            {
                params: {
                    group_ids: chunk.join(','),
                    ...(delegators ? { delegators } : {}),
                },
            },
        )),
    );
    return responses.flatMap(toApiDataArray);
}

export async function acceptTimeEntryGroup(groupId, payload = {}) {
    const response = await axiosTemp().patch(
        `/api/v1.0/accounts/ACCOUNT_ID/time-entry-groups/${groupId}/`,
        JSON.stringify({ ...payload, status: 'accepted' }),
    );
    return {
        ...response,
        data: {
            ...response.data,
            id: response.data?.time_entry_id,
        },
    };
}

export async function batchUpdateTimeEntryGroups(payload) {
    const response = await axiosTemp().patch(
        `/api/v1.0/accounts/ACCOUNT_ID/time-entry-groups/`,
        JSON.stringify(payload),
    );
    return response.data;
}

export async function updateTimeEntryGroup(groupId, payload) {
    const response = await axiosTemp().patch(
        `/api/v1.0/accounts/ACCOUNT_ID/time-entry-groups/${groupId}/`,
        JSON.stringify(payload),
    );
    return response.data;
}

export async function generateTimeEntryGroupDrafts(payload = {}) {
    const response = await axiosTemp().post(
        `/api/v1.0/accounts/ACCOUNT_ID/time-entry-group-generations/`,
        JSON.stringify(payload),
    );
    return response.data;
}

export async function getTimeEntryGroupGenerationStatus(params = {}) {
    const response = await axiosTemp().get(
        `/api/v1.0/accounts/ACCOUNT_ID/time-entry-group-generations/`,
        { params },
    );
    return response.data;
}

export async function getTimeEntryGroupGeneration(generationId) {
    const response = await axiosTemp().get(
        `/api/v1.0/accounts/ACCOUNT_ID/time-entry-group-generations/${generationId}/`,
    );
    return response.data;
}

export async function mergeTimeSlotSuggest(payload) {
    return  await axiosTemp().post(
        `/api/v1.0/accounts/ACCOUNT_ID/time-slots/merge/suggest/`,
        JSON.stringify(payload), 
    );
}

export async function getTimeSlots(query, signal) {
  try {
      const response = await axiosTemp().get(`/api/v1.0/accounts/ACCOUNT_ID/time-slots/${query}`, { signal });
      return response.data;
  } catch (error) {
      console.error('Error fetching time entries:', error);
      throw error;
  }
}

export async function getActionRecord(payload) {
  try {
      const response = await axiosTemp().post(`/api/v1.0/accounts/ACCOUNT_ID/action-records/`,  JSON.stringify(payload));
      return response.data;
  } catch (error) {
      console.error('Error fetching time entries:', error);
      throw error;
  }
}

export async function createTimeSlots(payload) {
  try {
      const response = await axiosTemp().post(`/api/batch/v1.0/accounts/ACCOUNT_ID/time-slots/`,  JSON.stringify(payload));
      return response.data;
  } catch (error) {
      console.error('Error fetching time entries:', error);
      throw error;
  }
}

export async function deleteTimeSlots(payload) {
  return await axiosTemp().delete(`/api/batch/v1.0/accounts/ACCOUNT_ID/time-slots/`, {
    data: payload,
  });
}

export async function updateTimeSlot(payload) {
    return  await axiosTemp().patch(
        `/api/v1.0/accounts/ACCOUNT_ID/time-slots/`,
        JSON.stringify(payload), 
    );
}

export async function getIdleSlots(query) {
  try {
      const response = await axiosTemp().get(`/api/v1.0/accounts/ACCOUNT_ID/idle-slots/${query}`);
      return response.data;
  } catch (error) {
      console.error('Error fetching time entries:', error);
      throw error;
  }
}

export async function deleteIdleSlots(payload) {
  return await axiosTemp().delete(`/api/batch/v1.0/accounts/ACCOUNT_ID/idle-slots/`, {
    data: payload,
  });
}

export async function updateIdleSlots(payload) {
    return  await axiosTemp().patch(
        `/api/v1.0/accounts/ACCOUNT_ID/idle-slots/`,
        JSON.stringify(payload), 
    );
}

export async function getCodeOptions(signal) {
    try {
        const response = await axiosTemp().get(`/api/v1.0/accounts/ACCOUNT_ID/code/`, { signal });
        return response.data;
    } catch (error) {
        console.error('Error fetching code:', error);
        throw error;
    }
}

export async function getAiCompletions(payload) {
    return  await axiosTemp().post(
        `/api/v1.0/accounts/ACCOUNT_ID/ai/completions/`,
        JSON.stringify(payload), 
    );
}

export async function consolidateTimeEntries() {
    return  await axiosTemp().post(
        `/api/v1.0/accounts/ACCOUNT_ID/time-entries/consolidate/`,
    );
}

/**
 * 格式化 Solr 查询关键字，避免非法字符
 * @param {string} input
 * @returns {string}
 */
export function formatSolrKeyword(input, fallback = "*") {
  if (!input || typeof input !== "string") return fallback;

  const trimmed = input.trim();

  let escaped = trimmed.replace(
    /([+\-&|!(){}\[\]^"~*?:\\\/\s])/g,
    "\\$1"
  );

  // 2. URL-style encode 特殊字符（顺序很重要）
  escaped = escaped
    .replace(/&/g, "%26")
    .replace(/\+/g, "%2B")
    .replace(/%(?!26|2B)/g, "%5C%25"); // 不替换已经被转义的 %26 或 %2B


  // 3. 包裹 wildcard *
  return escaped;
}

export async function getRecentMatters(delegatorId) {
    try {
        const response = await axiosTemp().get(`/api/v1.0/accounts/ACCOUNT_ID/recent-matters/`, {
            params: delegatorId == null ? {} : { delegators: delegatorId },
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching code:', error);
        throw error;
    }
}


/**
 * 通用搜索方法（支持 Solr 格式 q 参数）
 * @param {string} objectType - 类型（"matter" | "client"）
 * @param {string} keywords - 搜索关键字
 * @param {number} start - 起始位置（默认 0）
 * @param {number} rows - 返回条数（默认 20）
 * @param {string=} fl - 可选字段返回列表（例："name,number"）
 * @param {string=} extraConditions - 固定 Solr 查询条件（例："is_active:true"）
 * @returns {Promise<{ data: Array, total: number }>}
 */
export async function searchObjects(
  objectType,
  keywords = "",
  start = 0,
  rows = 20,
  fl,
  extraConditions = "",
  hasDestination = true,
  fq
) {
  try {
    let payload;
    const formattedKeyword = formatSolrKeyword(keywords);

      let q = extraConditions
          ? `${extraConditions} AND (s_text:(*${formattedKeyword}*))`
          : `(s_text:(*${formattedKeyword}*))`;

      payload = {
          start,
          rows,
          q
      };
      if (objectType) {
          payload.object_type = objectType;
          if(objectType == 'templatenarrative') {
            payload.q = extraConditions;
            payload.sort = 'updated_on desc';
          } else if(objectType == 'matter') {
            const nameNumberCondition = `(((number:*${formattedKeyword}*) OR (name:*${formattedKeyword}*) OR (client_name:*${formattedKeyword}*)) AND is_active:true)`;
            payload.q = extraConditions 
                ? `${extraConditions} AND ${nameNumberCondition}` 
                : nameNumberCondition;
          }
      }
      if (fl) {
          payload.fl = fl;
      }
      if (fq) {
          payload.fq = fq;
      }

    let user = JSON.parse(localStorage.getItem("user"));
    let delegators = sessionStorage.getItem("delegators");
    if (user && delegators && +user.id !== +delegators) {
        payload["delegators"] = [+delegators];
    }


    const response = await axiosTemp().post(`/api/v1.0/accounts/ACCOUNT_ID/search/`, payload);
    return response.data;
  } catch (error) {
    console.error(`❌ Error searching ${objectType}:`, error);
    return { data: [], total: 0 };
  }
}


export async function fetchServices() {
    try {
        const response = await axiosTemp().get(`/api/v1.0/accounts/ACCOUNT_ID/services/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching code:', error);
        throw error;
    }
}

export async function downloadInstaller(installer) {
  try {
    let platformStr = 'macOS';
    let arch = 'x64'; 

    if (navigator.userAgentData) {
      const data = await navigator.userAgentData.getHighEntropyValues(['architecture', 'platform']);
      platformStr = data.platform; // e.g., "macOS"
      arch = data.architecture;    // e.g., "arm" 或 "x86"
    }
    const response = await axiosTemp().get(`/api/v1.0/download/installer/?installer=${installer}&platform=${platformStr}&arch=${arch}` , {
      responseType: 'blob',
    });
    return response; // 返回完整 response，含 headers
  } catch (error) {
    console.error('下载失败', error);
    throw error;
  }
}

export async function fetchSetting(query) {
    try {
        const response = await axiosTemp().get(`/api/v1.0/accounts/ACCOUNT_ID/application-settings/` + query);
        return response.data;
    } catch (error) {
        console.error('Error fetching code:', error);
        throw error;
    }
}

export async function patchSetting(pk, payload) {
    try {
        const response = await axiosTemp().patch(`/api/v1.0/accounts/ACCOUNT_ID/application-settings/${pk}/`, JSON.stringify(payload));
        return response.data;
    } catch (error) {
        console.error('Error fetching code:', error);
        throw error;
    }
}

export async function fetchDelegations() {
    try {
        const response = await axiosTemp().get(`/api/v1.0/accounts/ACCOUNT_ID/delegations/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching code:', error);
        throw error;
    }
}

export async function fetchUsers() {
    try {
        const response = await axiosTemp().get(`/api/v1.0/accounts/ACCOUNT_ID/users/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching code:', error);
        throw error;
    }
}

export async function fetchUser(pk) {
    try {
        const response = await axiosTemp().get(`/api/v1.0/accounts/ACCOUNT_ID/users/${pk}/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching user:', error);
        throw error;
    }
}

export async function patchUser(pk, payload) {
    try {
        const response = await axiosTemp().patch(`/api/v1.0/accounts/ACCOUNT_ID/users/${pk}/`, JSON.stringify(payload));
        return response.data;
    } catch (error) {
        console.error('Error fetching code:', error);
        throw error;
    }
}

export async function getHoursByUserView(user_id, start_date, end_date, fl) {
    let query = `?user_id=${user_id}&start_date=${toLocalStartOfDay(start_date)}&end_date=${toLocalEndOfDay(end_date)}&local_start_dt=${start_date}&local_end_dt=${end_date}&fl=${fl}`
    try {
        const response = await axiosTemp().get(`/api/v1.0/accounts/ACCOUNT_ID/dashboard/hours-by-user/${query}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching code:', error);
        throw error;
    }
}

export async function getHoursByAppsView(user_id, start_date, end_date) {
    let query = `?user_id=${user_id}&start_date=${toLocalStartOfDay(start_date)}&end_date=${toLocalEndOfDay(end_date)}`
    try {
        const response = await axiosTemp().get(`/api/v1.0/accounts/ACCOUNT_ID/dashboard/hours-by-apps/${query}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching code:', error);
        throw error;
    }
}

export async function getHoursByClientsView(user_id, start_date, end_date) {
    let query = `?user_id=${user_id}&start_date=${toLocalStartOfDay(start_date)}&end_date=${toLocalEndOfDay(end_date)}`
    try {
        const response = await axiosTemp().get(`/api/v1.0/accounts/ACCOUNT_ID/dashboard/hours-by-clients/${query}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching code:', error);
        throw error;
    }
}

export async function getHoursByMattersView(user_id, start_date, end_date, client_id) {
    let query = `?user_id=${user_id}&start_date=${toLocalStartOfDay(start_date)}&end_date=${toLocalEndOfDay(end_date)}`;
    if (client_id) {
        query += `&client_id=${client_id}`
    }
    try {
        const response = await axiosTemp().get(`/api/v1.0/accounts/ACCOUNT_ID/dashboard/hours-by-matters/${query}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching code:', error);
        throw error;
    }
}

export async function fetchScheduledMaintenance() {
    try {
        const response = await axiosTemp().get(`/api/v1.0/landing-contents/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching code:', error);
        throw error;
    }
}

export async function postPrebillReport(payload) {
    try {
        const response = await axiosTemp().get(
            `/api/v1.0/accounts/ACCOUNT_ID/rpt/wip-report/`,
            { params: payload },
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching prebill report:', error);
        throw error;
    }
}


export async function fetchAliveApps(query) {
    try {
        const response = await axiosTemp().get(`/api/v1.0/accounts/ACCOUNT_ID/alive-apps/${query}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching code:', error);
        throw error;
    }
}
export async function fetchCaptureRules(query) {
    try {
        const response = await axiosTemp().get(`/api/v1.0/accounts/ACCOUNT_ID/capture-rules/${query}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching code:', error);
        throw error;
    }
}

export async function createCaptureRules(payload) {
  try {
      const response = await axiosTemp().post(`/api/v1.0/accounts/ACCOUNT_ID/capture-rules/`,  JSON.stringify(payload));
      return response.data;
  } catch (error) {
      console.error('Error fetching capture-rules:', error);
      throw error;
  }
}

export async function deleteCaptureRule(id) {
  return await axiosTemp().delete(`/api/v1.0/accounts/ACCOUNT_ID/capture-rules/${id}/`);
}

export async function updateCaptureRules(payload) {
    return  await axiosTemp().patch(
        `/api/v1.0/accounts/ACCOUNT_ID/capture-rules/`,
        JSON.stringify(payload), 
    );
}
